<script lang="ts" setup>
import ChevronUp24Icon from 'assets/icons/arrows/24/chevron-down.svg?component'
import useCatalogStore from '~/shared/store/modules/catalog'
import AppInput from '~/components/ui/form/formElements/AppInput.vue'
import WatchMoreAccordion from '~/components/ui/accordion/WatchMoreAccordion.vue'
import AppCheckbox from '~~/components/ui/form/formElements/AppCheckbox.vue'
import {
  onAccordionTransitionEnter,
  onAccordionAfterEnter,
  onAccordionTransitionLeave,
} from '~/components/ui/accordion/accordion-script'
import type {
  ICatalogFilter,
  ICatalogFilterValue,
} from '~/shared/interfaces/catalog'
import SmallAccordion from '~/components/ui/accordion/SmallAccordion.vue'

const props = defineProps<{
  filter: ICatalogFilter
}>()

const emits = defineEmits<{
  'set-filter': [
    {
      value: string
      active: boolean
      name: string
      filterId: string
      filterName: string
      isBrand?: boolean
    }
  ]
}>()

const catalogStore = useCatalogStore()
const isBrand = props.filter.id == 'brands'

const showOnStartCount = 5
const filterIsOpen = ref(true)
const fullListIsOpen = ref(false)
const filterSearch = ref()
const inputValue = ref('')

function toggleFilter() {
  filterIsOpen.value = !filterIsOpen.value
  filterSearch.value?.setInputValue('')
}

function toggleWatchMoreAccordion(isOpen: boolean) {
  fullListIsOpen.value = isOpen
  filterSearch.value?.setInputValue('')
}

function setFilterStatus(value: string, active: boolean, name: string) {
  const { id, title } = props.filter

  emits('set-filter', {
    value,
    active,
    name,
    isBrand,
    filterId: id,
    filterName: title,
  })
}

const getFiltersListData = computed(() => {
  const filter = { ...props.filter }
  if (filter.sortedByAlphabetValues && fullListIsOpen.value) {
    if (inputValue.value) {
      const result = {} as { [key: string]: ICatalogFilterValue[] }
      for (const key in filter.sortedByAlphabetValues) {
        const values = filter.sortedByAlphabetValues[key].filter(({ title }) =>
          title?.includes(inputValue.value)
        )
        if (values.length) {
          result[key] = values
        }
      }
      return result
    }
    return filter.sortedByAlphabetValues
  } else {
    const valuesOnShow = filter.values
    if (inputValue.value) {
      return valuesOnShow.filter(({ value }) =>
        value.toLowerCase().includes(inputValue.value.toLowerCase())
      )
    }
    return valuesOnShow
  }
})

function toggleSmallAccordion(event: boolean) {
  fullListIsOpen.value = event
}
</script>

<template>
  <div class="filters">
    <div class="filters-header" @click="toggleFilter">
      <span class="filters-header__title">{{ filter.title }}</span>
      <ChevronUp24Icon
        class="filters-header__icon"
        :class="filterIsOpen && 'filters-header__icon_active'"
      />
    </div>
    <Transition
      name="accordion-item"
      @enter="onAccordionTransitionEnter"
      @leave="onAccordionTransitionLeave"
      @after-enter="onAccordionAfterEnter"
    >
      <div v-show="filterIsOpen" class="filters-wrapper">
        <AppInput
          ref="filterSearch"
          class="filters-input"
          v-show="fullListIsOpen"
          v-model="inputValue"
          :input-config="{
            id: filter.id + '_input',
            placeholder: 'Найти',
            size: 'xs',
            hasClear: true,
          }"
        />
        <div
          class="filters-list"
          :class="filter.id === 'brands' && 'filters-list_brands'"
        >
          <SmallAccordion
            :show-on-start-count="showOnStartCount"
            :gap="8"
            @toggle-accordion="toggleSmallAccordion"
            :items-length="getFiltersListData.length as number"
          >
            <template v-if="Array.isArray(getFiltersListData)" #content>
              <div
                v-if="!getFiltersListData.length"
                class="filters-list__empty"
              >
                Ничего не найдено
              </div>
              <div
                v-for="{ value, active, count, title } in getFiltersListData"
                class="filters-list__item"
                :class="active && 'active'"
              >
                <AppCheckbox
                  class="filters-list__item-checkbox"
                  :checkbox-id="value"
                  :checkbox-label="title || value"
                  :priority-checkbox-value="active"
                  :checkbox-disabled="!count"
                  @update:model-value="
                    setFilterStatus(value, !active, title || value)
                  "
                  checkbox-label-distance="small-distance"
                />
                <span
                  class="filters-list__item-count filters-list__item-count_checkbox"
                >
                  {{ count }}
                </span>
              </div>
            </template>
          </SmallAccordion>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.filters {
  &-input {
    margin: 2px 0 12px;
  }
  &-list {
    text-transform: capitalize;

    :deep(.accordion-list) {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &_brands {
      text-transform: uppercase;
    }

    &__empty {
      @include font(14px, 20px);
      color: $content_secondary;
      text-transform: none;
    }

    &__letter {
      padding-top: 8px;
      color: $control-content_primary;
      @include font(16px, 20px, 500);
    }
    &__item {
      display: flex;
      align-items: center;
      gap: 4px;

      &.active {
        order: -1;
      }

      &-wrapper {
        &:first-child {
          .filters-list__letter {
            padding-top: 0;
          }
        }
      }
      &-count {
        display: inline-block;
        margin-left: 4px;
        color: $control-content_secondary;
        @include font(12px, 16px);

        &_checkbox {
          margin: 0;
        }
      }
      &:first-child {
        padding-top: 0;
      }
    }
    &:deep(.accordion-hidden__wrapper) {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &:deep(.accordion-list) {
      width: calc(100% - 4px);
      max-height: 372px;
      overflow: auto;
      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: $control-content_tretiary;
        border-radius: 4px;
      }

      &::-webkit-scrollbar-button {
        display: none;
        visibility: hidden;
      }
    }
    &:deep(.watch-more) {
      display: block;
      border: none;
      background-color: transparent;
      padding: 8px 0 0;
      margin-bottom: 0;
      color: $control-content_orange;
      @include font(14px, 20px, 400);
      &:hover:not(:active) {
        @include mf-breakpoint {
          color: $control-content_orange;
          background-color: transparent;
        }
      }
    }
  }
  &-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    cursor: pointer;
    &__icon {
      transform: rotate(0);
      transition: 0.2s transform;
      &_active {
        transform: rotate(180deg);
      }
    }
    &__title {
      color: $control-content_primary;
      @include font(16px, 20px, 500);
    }
  }
}
</style>
